import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { NavigationSource, servicePage, skills } from '../../constants/routes';
import { useAppendHubReturnPath } from '../../hooks/useHubReturnPath';
import { useProfileRoutes } from '../../hooks/useProfileRoutes';
import { ReturnQueryParam } from '../../types/ServiceForm';

type useBackRouteParamsReturn = {
  backRoute: string;
  onSuccessRedirect: () => void;
  isFullPageRedirect: boolean;
};

const useBackRouteParams = (returnQueryParam: ReturnQueryParam): useBackRouteParamsReturn => {
  const navigate = useNavigate();
  const routes = useProfileRoutes();
  const appendHubReturnPath = useAppendHubReturnPath();
  const defaultBackRoute = routes.details;

  const [baseBackRoute, setBackRoute] = React.useState<string>(defaultBackRoute);
  const [redirectServiceType, setRedirectServiceType] = React.useState<string>('');

  // Set back url based on source query param
  useEffect(() => {
    // check for query params to set back url
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    const serviceType = params.get('serviceType');

    if (source === NavigationSource.ServiceForm && serviceType) {
      setBackRoute(servicePage(serviceType));
      setRedirectServiceType(serviceType);
    }

    if (source === NavigationSource.Skills) {
      setBackRoute(routes.skills);
    }

    // Clean up URL by removing source and serviceType params
    if (source) {
      params.delete('source');
    }
    if (serviceType) {
      params.delete('serviceType');
    }

    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [routes.skills, routes.details]);

  const isSkillsRoute = baseBackRoute === skills || baseBackRoute.includes('/details/skills');

  // Callers that fire analytics before redirecting need to know whether the page
  // is about to unload, since a deferred dataLayer push would not survive it.
  const isFullPageRedirect =
    !isSkillsRoute && !!redirectServiceType && baseBackRoute === servicePage(redirectServiceType);

  // The page this one returns to reads `return_to` for its own exit, so it goes along.
  const backRoute = appendHubReturnPath(baseBackRoute);

  const onSuccessRedirect = useCallback((): void => {
    const backUrl = appendHubReturnPath(`${baseBackRoute}?${returnQueryParam}=true`);

    if (isSkillsRoute) {
      // Skills page is a React Router page
      navigate(backUrl);
    } else if (isFullPageRedirect) {
      // Service page is a Django template, we need full page reload
      window.location.assign(backUrl);
    } else {
      // Default to React Router navigation (go back to sitter profile or EPSU details)
      navigate(backRoute);
    }
  }, [
    isSkillsRoute,
    isFullPageRedirect,
    returnQueryParam,
    navigate,
    backRoute,
    baseBackRoute,
    appendHubReturnPath,
  ]);

  return { backRoute, onSuccessRedirect, isFullPageRedirect };
};

export default useBackRouteParams;
